import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import type { AuthContext, LoginArgs } from './types'
import type { User } from '../../types'
import { getUserFromStorage, setUserInStorage } from './utils'
import axios from 'axios'

const Context = createContext({} as AuthContext)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<null | User>(null)
  const [isActive, setIsActive] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const [reFetch, setReFetch] = useState(0)

  useEffect(() => {
    const storedUser = getUserFromStorage()
    if (storedUser?.id) {
      setUser(storedUser)
    }

    fetchUser().finally(() => setIsLoading(false))
  }, [reFetch])

  const fetchUser = async () => {
    try {
      const {
        data: { user, isActive },
      } = await axios.get(`/api-me`, {
        withCredentials: true,
      })

      setIsActive(isActive)

      if (user.id) {
        setUser(user)
        setUserInStorage(user)
      } else {
        setUser(null)
        setUserInStorage(null)
      }
    } catch (error) {
      setUser(null)
      setUserInStorage(null)
      console.log(error)
    }
  }

  const login = useCallback(async (args: LoginArgs) => {
    const {
      data: { user },
    } = await axios.post(`/api-login`, args, {
      withCredentials: true,
    })
    setUser(user)
    setUserInStorage(user)

    return user
  }, [])

  const logout = useCallback(async () => {
    await axios.get(`/api-logout`)

    setUser(null)
    setUserInStorage(null)
  }, [])

  return (
    <Context.Provider
      value={{
        login,
        logout,
        user,
        isLoading,
        refresh: () => setReFetch((prev) => prev + 1),
        isActive,
      }}
    >
      {children}
    </Context.Provider>
  )
}

type UseAuth = () => AuthContext

export const useAuth: UseAuth = () => useContext(Context)
